import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet-rotate';
import {
  AttributionControl,
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  TileLayer,
  ZoomControl,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { placeLabel } from '../../../../utils/formatters';
import { findBuildingSnapPoint, loadNearbyBuildingLines } from '../../../../services/osmBuildings';
import {
  Button,
  SvgIcon,
  Typography,
  Link,
  MenuItem,
  Stack,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
export const defaultLocation = {
  lat: 32.0853,
  lng: 34.7818,
};
export const defaultMapZoom = 20;
export const minMapZoom = 14;
export const maxMapZoom = 22;
export const maxTileZoom = 20;
const getLocation = (placeForm) => {
  const lat = Number(placeForm.lat);
  const lng = Number(placeForm.lng);
  return {
    lat: Number.isFinite(lat) ? lat : defaultLocation.lat,
    lng: Number.isFinite(lng) ? lng : defaultLocation.lng,
    hasValue: Number.isFinite(lat) && Number.isFinite(lng),
  };
};
const getGoogleMapsUrl = (placeForm, location) => {
  if (!location.hasValue) return '';
  const coordinates = location.hasValue ? `${location.lat},${location.lng}` : '';
  const placeName = placeForm.name?.he || placeForm.name?.en || '';
  const query = placeName || coordinates;
  const params = new URLSearchParams({
    api: '1',
    query,
  });
  return `https://www.google.com/maps/search/?${params.toString()}`;
};
const locationIcon = L.divIcon({
  className: 'leaflet-location-marker',
  html: '<span aria-hidden="true"></span>',
  iconSize: [28, 36],
  iconAnchor: [14, 36],
});
const vertexIcon = L.divIcon({
  className: 'leaflet-polygon-vertex',
  html: '<span aria-hidden="true"></span>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});
const rotationHandleIcon = L.divIcon({
  className: 'leaflet-polygon-rotation-handle',
  html: '<span aria-hidden="true">↻</span>',
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});
const scaleHandleIcon = L.divIcon({
  className: 'leaflet-polygon-scale-handle',
  html: '<span aria-hidden="true">↗</span>',
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});
const getRotationGeometry = (points) => {
  if (!points || points.length < 3) return null;
  const center = points.reduce(
    (result, [lng, lat]) => ({
      lng: result.lng + lng / points.length,
      lat: result.lat + lat / points.length,
    }),
    {
      lng: 0,
      lat: 0,
    },
  );
  const lngScale = Math.max(Math.cos((center.lat * Math.PI) / 180), 0.2);
  const radius = Math.max(
    ...points.map(([lng, lat]) => Math.hypot((lng - center.lng) * lngScale, lat - center.lat)),
  );
  const handleRadius = Math.max(radius * 1.08, 0.00008);
  return {
    center,
    lngScale,
    handle: [center.lat, center.lng + handleRadius / lngScale],
    scaleHandle: [
      center.lat - handleRadius * 0.707,
      center.lng + (handleRadius * 0.707) / lngScale,
    ],
  };
};
const distanceToSegment = (point, start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) return point.distanceTo(start);
  const projection = Math.max(
    0,
    Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)),
  );
  const closest = L.point(start.x + projection * dx, start.y + projection * dy);
  return point.distanceTo(closest);
};
const pointOnSegment = (point, start, end) => {
  const cross =
    (point[1] - start[1]) * (end[0] - start[0]) - (point[0] - start[0]) * (end[1] - start[1]);
  if (Math.abs(cross) > 1e-10) return false;
  return (
    point[0] >= Math.min(start[0], end[0]) &&
    point[0] <= Math.max(start[0], end[0]) &&
    point[1] >= Math.min(start[1], end[1]) &&
    point[1] <= Math.max(start[1], end[1])
  );
};
const segmentsIntersect = (startA, endA, startB, endB) => {
  const direction = (a, b, c) => (c[0] - a[0]) * (b[1] - a[1]) - (c[1] - a[1]) * (b[0] - a[0]);
  const d1 = direction(startA, endA, startB);
  const d2 = direction(startA, endA, endB);
  const d3 = direction(startB, endB, startA);
  const d4 = direction(startB, endB, endA);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0)))
    return true;
  return (
    (Math.abs(d1) <= 1e-10 && pointOnSegment(startB, startA, endA)) ||
    (Math.abs(d2) <= 1e-10 && pointOnSegment(endB, startA, endA)) ||
    (Math.abs(d3) <= 1e-10 && pointOnSegment(startA, startB, endB)) ||
    (Math.abs(d4) <= 1e-10 && pointOnSegment(endA, startB, endB))
  );
};
const pointInRing = (point, ring) => {
  let inside = false;
  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
    const current = ring[index];
    const before = ring[previous];
    if (pointOnSegment(point, before, current)) return true;
    const crosses =
      current[1] > point[1] !== before[1] > point[1] &&
      point[0] <
      ((before[0] - current[0]) * (point[1] - current[1])) /
      (before[1] - current[1] || Number.EPSILON) +
      current[0];
    if (crosses) inside = !inside;
  }
  return inside;
};
const ringsOverlap = (firstRing, secondRing) => {
  if (firstRing.length < 3 || secondRing.length < 3) return false;
  for (let firstIndex = 0; firstIndex < firstRing.length; firstIndex += 1) {
    const firstNext = (firstIndex + 1) % firstRing.length;
    for (let secondIndex = 0; secondIndex < secondRing.length; secondIndex += 1) {
      const secondNext = (secondIndex + 1) % secondRing.length;
      if (
        segmentsIntersect(
          firstRing[firstIndex],
          firstRing[firstNext],
          secondRing[secondIndex],
          secondRing[secondNext],
        )
      )
        return true;
    }
  }
  return pointInRing(firstRing[0], secondRing) || pointInRing(secondRing[0], firstRing);
};
const ringArea = (ring) => {
  let area = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const next = ring[(index + 1) % ring.length];
    area += ring[index][0] * next[1] - next[0] * ring[index][1];
  }
  return Math.abs(area / 2);
};
const angleDistance = (first, second) => {
  const difference = Math.abs(first - second) % Math.PI;
  return Math.min(difference, Math.PI - difference);
};
const snapPointToPolygonDirections = (points, movingIndex, candidate, thresholdDegrees = 8) => {
  if (points.length < 2) return null;
  const referenceLatitude = Number(candidate[1]);
  const longitudeScale = Math.max(Math.cos((referenceLatitude * Math.PI) / 180), 0.2);
  const toLocal = ([lng, lat]) => [lng * longitudeScale, lat];
  const fromLocal = ([x, y]) => [x / longitudeScale, y];
  const localPoints = points.map(toLocal);
  const localCandidate = toLocal(candidate);
  const referenceAngles = [];
  for (let index = 0; index < points.length; index += 1) {
    const nextIndex = (index + 1) % points.length;
    if (points.length < 3 && nextIndex === 0) continue;
    if (index === movingIndex || nextIndex === movingIndex) continue;
    const start = localPoints[index];
    const end = localPoints[nextIndex];
    if (!start || !end) continue;
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    if (Math.hypot(dx, dy) < 1e-9) continue;
    const angle = Math.atan2(dy, dx);
    referenceAngles.push(angle, angle + Math.PI / 2);
  }
  if (!referenceAngles.length) return null;
  const anchorIndexes =
    movingIndex >= points.length
      ? [points.length - 1]
      : [(movingIndex - 1 + points.length) % points.length, (movingIndex + 1) % points.length];
  const threshold = (thresholdDegrees * Math.PI) / 180;
  let best = null;
  anchorIndexes.forEach((anchorIndex) => {
    const anchor = localPoints[anchorIndex];
    if (!anchor) return;
    const dx = localCandidate[0] - anchor[0];
    const dy = localCandidate[1] - anchor[1];
    const length = Math.hypot(dx, dy);
    if (length < 1e-9) return;
    const candidateAngle = Math.atan2(dy, dx);
    referenceAngles.forEach((referenceAngle) => {
      const difference = angleDistance(candidateAngle, referenceAngle);
      if (difference > threshold || (best && difference >= best.difference)) return;
      const direction =
        Math.cos(candidateAngle - referenceAngle) >= 0 ? referenceAngle : referenceAngle + Math.PI;
      best = {
        difference,
        point: fromLocal([
          anchor[0] + Math.cos(direction) * length,
          anchor[1] + Math.sin(direction) * length,
        ]),
      };
    });
  });
  return best;
};
const addPolygonPoint = (points, lng, lat, map, geometryMagnetEnabled = false) => {
  const rawPoint = [lng, lat];
  const geometrySnap = geometryMagnetEnabled
    ? snapPointToPolygonDirections(points, points.length, rawPoint)
    : null;
  const nextPoint = geometrySnap?.point || rawPoint;
  if (points.length < 2) return [...points, nextPoint];
  const clickPoint = map.latLngToLayerPoint([lat, lng]);
  const screenPoints = points.map(([pointLng, pointLat]) =>
    map.latLngToLayerPoint([pointLat, pointLng]),
  );
  const segmentCount = points.length >= 3 ? points.length : points.length - 1;
  let closestSegment = -1;
  let closestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < segmentCount; index += 1) {
    const nextIndex = (index + 1) % points.length;
    const distance = distanceToSegment(clickPoint, screenPoints[index], screenPoints[nextIndex]);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestSegment = index;
    }
  }
  if (closestSegment === -1 || closestDistance > 24) {
    return [...points, nextPoint];
  }
  const updated = [...points];
  updated.splice(closestSegment + 1, 0, nextPoint);
  return updated;
};
export const footprintRings = (footprint) => {
  if (footprint?.type === 'Polygon') return [footprint.coordinates?.[0] || []];
  if (footprint?.type === 'MultiPolygon') {
    return (footprint.coordinates || []).map((polygon) => polygon?.[0] || []);
  }
  return [];
};
const updateFootprintPart = (current, partIndex, updater) => {
  const parts = current.footprintParts?.length
    ? current.footprintParts.map((part) => [...part])
    : [current.footprintPoints || []];
  while (parts.length <= partIndex) parts.push([]);
  parts[partIndex] = updater(parts[partIndex] || []);
  return {
    ...current,
    footprintParts: parts,
    footprintPoints: parts[partIndex],
  };
};
const shapePointsFromDrag = (map, shape, startLatLng, endLatLng) => {
  const start = map.latLngToLayerPoint(startLatLng);
  const end = map.latLngToLayerPoint(endLatLng);
  let dx = end.x - start.x;
  let dy = end.y - start.y;
  if (shape === 'square') {
    const size = Math.max(Math.abs(dx), Math.abs(dy));
    dx = Math.sign(dx || 1) * size;
    dy = Math.sign(dy || 1) * size;
  }
  if (shape === 'circle') {
    const radius = start.distanceTo(end);
    return Array.from(
      {
        length: 48,
      },
      (_, index) => {
        const angle = (index / 48) * Math.PI * 2;
        const latLng = map.layerPointToLatLng(
          L.point(start.x + Math.cos(angle) * radius, start.y + Math.sin(angle) * radius),
        );
        return [Number(latLng.lng.toFixed(7)), Number(latLng.lat.toFixed(7))];
      },
    );
  }
  return [
    start,
    L.point(start.x + dx, start.y),
    L.point(start.x + dx, start.y + dy),
    L.point(start.x, start.y + dy),
  ].map((point) => {
    const latLng = map.layerPointToLatLng(point);
    return [Number(latLng.lng.toFixed(7)), Number(latLng.lat.toFixed(7))];
  });
};
const MapEditorEvents = ({
  editMode,
  setPlaceForm,
  selectBuildingMode,
  buildingLines,
  onSelectBuilding,
  activePartIndex,
  geometryMagnetEnabled,
  polygonDragMode,
  interactionLocked,
  shapeMode,
  onShapeComplete,
  onBeforeChange,
}) => {
  const map = useMap();
  const polygonDragRef = useRef(null);
  const shapeDragRef = useRef(null);
  const suppressClickRef = useRef(false);
  useMapEvents({
    click(event) {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      if (shapeMode) return;
      if (polygonDragMode || interactionLocked) return;
      if (selectBuildingMode) {
        const clickedPoint = [event.latlng.lng, event.latlng.lat];
        const selectedBuilding = buildingLines
          .filter((line) => line.isClosed && pointInRing(clickedPoint, line.points))
          .sort((first, second) => ringArea(first.points) - ringArea(second.points))[0];
        if (selectedBuilding) onSelectBuilding(selectedBuilding);
        return;
      }
      const lat = Number(event.latlng.lat.toFixed(6));
      const lng = Number(event.latlng.lng.toFixed(6));
      onBeforeChange();
      setPlaceForm((current) =>
        editMode === 'frontage'
          ? (current.frontagePoints || []).length >= 2
            ? current
            : {
              ...current,
              frontagePoints: [...(current.frontagePoints || []), [lng, lat]],
            }
          : editMode === 'polygon'
            ? {
              ...current,
              ...updateFootprintPart(current, activePartIndex, (points) =>
                addPolygonPoint(points, lng, lat, map, geometryMagnetEnabled),
              ),
            }
            : {
              ...current,
              lat: lat.toFixed(6),
              lng: lng.toFixed(6),
            },
      );
    },
    mousedown(event) {
      if (shapeMode) {
        shapeDragRef.current = {
          start: event.latlng,
          startLayerPoint: map.latLngToLayerPoint(event.latlng),
          changed: false,
        };
        map.dragging.disable();
        return;
      }
      if (!polygonDragMode) return;
      polygonDragRef.current = {
        lat: event.latlng.lat,
        lng: event.latlng.lng,
        changed: false,
      };
      map.dragging.disable();
    },
    mousemove(event) {
      const shapeDrag = shapeDragRef.current;
      if (shapeMode && shapeDrag) {
        const distance = shapeDrag.startLayerPoint.distanceTo(map.latLngToLayerPoint(event.latlng));
        if (distance < 3) return;
        if (!shapeDrag.changed) {
          onBeforeChange();
          shapeDrag.changed = true;
        }
        const nextPoints = shapePointsFromDrag(map, shapeMode, shapeDrag.start, event.latlng);
        setPlaceForm((current) => updateFootprintPart(current, activePartIndex, () => nextPoints));
        return;
      }
      const drag = polygonDragRef.current;
      if (!polygonDragMode || !drag) return;
      if (!drag.changed) {
        onBeforeChange();
        drag.changed = true;
      }
      const deltaLng = event.latlng.lng - drag.lng;
      const deltaLat = event.latlng.lat - drag.lat;
      drag.lng = event.latlng.lng;
      drag.lat = event.latlng.lat;
      setPlaceForm((current) => {
        const sourceParts = current.footprintParts?.length
          ? current.footprintParts
          : [current.footprintPoints || []];
        const parts = sourceParts.map((part) =>
          part.map(([lng, lat]) => [
            Number((lng + deltaLng).toFixed(7)),
            Number((lat + deltaLat).toFixed(7)),
          ]),
        );
        return {
          ...current,
          footprintParts: parts,
          footprintPoints: parts[activePartIndex] || parts[0] || [],
        };
      });
    },
    mouseup() {
      if (shapeDragRef.current) {
        const completed = shapeDragRef.current.changed;
        shapeDragRef.current = null;
        map.dragging.enable();
        if (completed) {
          suppressClickRef.current = true;
          onShapeComplete();
        }
        return;
      }
      if (!polygonDragRef.current) return;
      polygonDragRef.current = null;
      map.dragging.enable();
    },
  });
  useEffect(() => {
    if (!polygonDragMode && polygonDragRef.current) {
      polygonDragRef.current = null;
      map.dragging.enable();
    }
  }, [map, polygonDragMode]);
  useEffect(() => {
    if (!shapeMode && shapeDragRef.current) {
      shapeDragRef.current = null;
      map.dragging.enable();
    }
  }, [map, shapeMode]);
  useEffect(() => {
    const container = map.getContainer();
    const handleWheel = (event) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      event.stopPropagation();
      map.setZoom(map.getZoom() + (event.deltaY < 0 ? 1 : -1));
    };
    container.addEventListener('wheel', handleWheel, {
      passive: false,
    });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [map]);
  useEffect(() => {
    const container = map.getContainer();
    let frameId = null;
    const refreshMapSize = () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        map.invalidateSize({
          animate: false,
          pan: false,
        });
        frameId = null;
      });
    };
    refreshMapSize();
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refreshMapSize);
    resizeObserver?.observe(container);
    window.addEventListener('resize', refreshMapSize);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', refreshMapSize);
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [map]);
  return null;
};
const MapFootprintFocus = ({ request }) => {
  const map = useMap();
  useEffect(() => {
    if (!request?.footprint) return;
    const points = footprintRings(request.footprint).flat();
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map(([lng, lat]) => [lat, lng]));
    map.fitBounds(bounds, {
      padding: [70, 70],
      maxZoom: 21,
      animate: false,
    });
  }, [map, request]);
  return null;
};
const MapBearingGestures = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    let drag = null;
    let suppressClick = false;
    const finish = () => {
      if (!drag) return;
      if (drag.restoreDragging) map.dragging.enable();
      drag = null;
      container.classList.remove('map-bearing-dragging');
    };
    const onMouseMove = (event) => {
      if (!drag) return;
      event.preventDefault();
      const delta = event.clientX - drag.startX;
      if (Math.abs(delta) > 2) suppressClick = true;
      map.setBearing(drag.startBearing + delta * 0.45);
    };
    const onMouseDown = (event) => {
      if (!(event.button === 2 || (event.button === 0 && event.ctrlKey))) return;
      event.preventDefault();
      event.stopPropagation();
      drag = {
        startX: event.clientX,
        startBearing: map.getBearing(),
        restoreDragging: map.dragging.enabled(),
      };
      if (drag.restoreDragging) map.dragging.disable();
      container.classList.add('map-bearing-dragging');
    };
    const onContextMenu = (event) => event.preventDefault();
    const onClickCapture = (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    container.addEventListener('mousedown', onMouseDown, true);
    container.addEventListener('contextmenu', onContextMenu);
    container.addEventListener('click', onClickCapture, true);
    document.addEventListener('mousemove', onMouseMove, {
      passive: false,
    });
    document.addEventListener('mouseup', finish);
    return () => {
      finish();
      container.removeEventListener('mousedown', onMouseDown, true);
      container.removeEventListener('contextmenu', onContextMenu);
      container.removeEventListener('click', onClickCapture, true);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', finish);
    };
  }, [map]);
  return null;
};
const MapToolIcon = ({ name }) => {
  const paths = {
    frontage: (
      <>
        <path d="M4 16 20 8" />
        <circle cx="4" cy="16" r="2" />
        <circle cx="20" cy="8" r="2" />
      </>
    ),
    location: (
      <>
        <path d="M12 21s6-5.2 6-11a6 6 0 1 0-12 0c0 5.8 6 11 6 11Z" />
        <circle cx="12" cy="10" r="2" />
      </>
    ),
    polygon: (
      <>
        <path d="m5 5 13 2-2 12-12-3Z" />
        <circle cx="5" cy="5" r="1.5" />
        <circle cx="18" cy="7" r="1.5" />
        <circle cx="16" cy="19" r="1.5" />
        <circle cx="4" cy="16" r="1.5" />
      </>
    ),
    align: (
      <>
        <path d="M4 8h16M4 16h16" />
        <path d="m7 5-3 3 3 3M17 13l3 3-3 3" />
      </>
    ),
    building: (
      <>
        <path d="M4 21V8l8-5 8 5v13M8 12h2M14 12h2M8 16h2M14 16h2" />
      </>
    ),
    select: (
      <>
        <path d="m5 3 11 8-6 1-3 6Z" />
        <path d="m12 13 5 6" />
      </>
    ),
    undo: (
      <>
        <path d="M9 7 4 12l5 5" />
        <path d="M5 12h8a6 6 0 0 1 6 6" />
      </>
    ),
    removeVertex: (
      <>
        <circle cx="7" cy="7" r="2" />
        <circle cx="17" cy="17" r="2" />
        <path d="m8.5 8.5 7 7M15 5h6M18 2v6" />
      </>
    ),
    hand: (
      <>
        <path d="M7 11V7a2 2 0 0 1 4 0v3-5a2 2 0 0 1 4 0v5-3a2 2 0 0 1 4 0v7c0 5-3 8-8 8H9c-3 0-5-2-6-5l-1-3a2 2 0 0 1 3-2l2 2" />
      </>
    ),
    rotate: (
      <>
        <path d="M20 11a8 8 0 1 0-2.3 5.7" />
        <path d="M20 4v7h-7" />
      </>
    ),
    scale: (
      <>
        <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
        <path d="m3 3 6 6M21 3l-6 6M3 21l6-6M21 21l-6-6" />
      </>
    ),
    trash: (
      <>
        <path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6" />
      </>
    ),
    shapes: (
      <>
        <rect x="3" y="4" width="8" height="8" rx="1" />
        <circle cx="16.5" cy="16.5" r="4.5" />
      </>
    ),
    square: <rect x="4" y="4" width="16" height="16" rx="1" />,
    rectangle: <rect x="3" y="6" width="18" height="12" rx="1" />,
    circle: <circle cx="12" cy="12" r="8" />,
  };
  return (
    <SvgIcon
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </SvgIcon>
  );
};
const MapToolButton = ({ label, icon, active = false, danger = false, sx, ...props }) => (
  <Tooltip title={label} placement="top" arrow>
    <span>
      <IconButton
        type="button"
        aria-label={label}
        aria-pressed={active}
        size="small"
        {...props}
        sx={[
          {
            width: 30,
            height: 25,
            border: '1px solid',
            borderColor: '#d0d5dd',
            borderRadius: '9px',
            color: '#667085',
            backgroundColor: '#f8fafc',
            boxShadow: 'none',
            '& svg': {
              width: 16,
              height: 16,
            },
            '&:hover': {
              color: '#344054',
              borderColor: '#98a2b3',
              backgroundColor: '#f2f4f7',
            },
            '&.Mui-disabled': {
              color: '#b8c0cc',
              borderColor: '#e4e7ec',
              backgroundColor: '#f9fafb',
              opacity: 0.65,
            },
          },
          active && {
            color: '#fff',
            borderColor: '#1570ef',
            backgroundColor: '#1570ef',
            boxShadow: '0 4px 10px rgba(21, 112, 239, 0.25)',
            '&:hover': {
              color: '#fff',
              borderColor: '#1570ef',
              backgroundColor: '#1264d8',
            },
          },
          danger && {
            color: '#d92d20',
            borderColor: '#d92d20',
            backgroundColor: '#fee4e2',
            '&:hover': {
              color: '#b42318',
              borderColor: '#b42318',
              backgroundColor: '#fecdca',
            },
          },
          active &&
          danger && {
            color: '#fff',
            borderColor: '#d92d20',
            backgroundColor: '#d92d20',
            '&:hover': {
              backgroundColor: '#b42318',
            },
          },
          sx,
        ]}
      >
        <MapToolIcon name={icon} />
      </IconButton>
    </span>
  </Tooltip>
);
export const PolygonEditor = ({
  placeForm,
  setPlaceForm,
  nearbyFootprints,
  nearbyFootprintsLoading,
  setFootprintCollisions,
  polygonOnly = false,
  originalPoints = [],
  showHeightControl = true,
  snapDistanceMeters = 1,
  allowMarkerMoveInPolygonMode = false,
  singlePart = false,
  polygonPanelContent = null,
  focusRequest = null,
}) => {
  const [editMode, setEditMode] = useState(polygonOnly ? 'polygon' : 'location');
  const [buildingLines, setBuildingLines] = useState([]);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [buildingMagnetEnabled, setBuildingMagnetEnabled] = useState(true);
  const [geometryMagnetEnabled, setGeometryMagnetEnabled] = useState(true);
  const [snappedVertexIndex, setSnappedVertexIndex] = useState(null);
  const [selectBuildingMode, setSelectBuildingMode] = useState(false);
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [undoHistory, setUndoHistory] = useState([]);
  const [vertexDeleteMode, setVertexDeleteMode] = useState(false);
  const [polygonDragMode, setPolygonDragMode] = useState(false);
  const [rotationMode, setRotationMode] = useState(false);
  const [scaleMode, setScaleMode] = useState(false);
  const [shapeMode, setShapeMode] = useState(null);
  const [shapeMenuOpen, setShapeMenuOpen] = useState(false);
  const dragHistoryCapturedRef = useRef(false);
  const rotationDragRef = useRef(null);
  const scaleDragRef = useRef(null);
  const footprintParts = useMemo(
    () =>
      placeForm.footprintParts?.length
        ? placeForm.footprintParts
        : [placeForm.footprintPoints || []],
    [placeForm.footprintParts, placeForm.footprintPoints],
  );
  const activePoints = useMemo(
    () => footprintParts[activePartIndex] || [],
    [footprintParts, activePartIndex],
  );
  const rotationGeometry = useMemo(() => getRotationGeometry(activePoints), [activePoints]);
  const location = getLocation(placeForm);
  const googleMapsUrl = getGoogleMapsUrl(placeForm, location);
  const footprintCollisions = useMemo(() => {
    const sameParent = (nearbyPlace) => {
      const currentParent = placeForm.containment || {};
      const nearbyParent = nearbyPlace.containment || {};
      return Boolean(
        currentParent.parentPlaceId && currentParent.parentPlaceId === nearbyParent.parentPlaceId,
      );
    };
    const belongsToCurrentContainer = (nearbyPlace) =>
      Boolean(placeForm.placeId && nearbyPlace.containment?.parentPlaceId === placeForm.placeId);
    const isRelevantCollision = (nearbyPlace) => {
      if (
        placeForm.placeKind === 'container' &&
        nearbyPlace.placeKind === 'unit' &&
        belongsToCurrentContainer(nearbyPlace)
      )
        return false;
      if (placeForm.placeKind === 'unit') {
        return (
          nearbyPlace.placeKind === 'unit' &&
          sameParent(nearbyPlace) &&
          String(nearbyPlace.containment?.floor || '') ===
          String(placeForm.containment?.floor || '')
        );
      }
      return true;
    };
    return footprintParts.some((part) => part.length >= 3)
      ? nearbyFootprints.filter(
        (nearbyPlace) =>
          isRelevantCollision(nearbyPlace) &&
          footprintParts.some(
            (part) =>
              part.length >= 3 &&
              footprintRings(nearbyPlace.footprint).some((ring) => ringsOverlap(part, ring)),
          ),
      )
      : [];
  }, [
    nearbyFootprints,
    footprintParts,
    placeForm.containment,
    placeForm.placeId,
    placeForm.placeKind,
  ]);
  useEffect(() => {
    setFootprintCollisions(footprintCollisions);
  }, [footprintCollisions, setFootprintCollisions]);
  useEffect(() => {
    if (!location.hasValue) {
      setBuildingLines([]);
      return undefined;
    }
    const controller = new AbortController();
    setBuildingsLoading(true);
    loadNearbyBuildingLines(location.lat, location.lng, controller.signal)
      .then((lines) => {
        setBuildingLines(lines);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setBuildingLines([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setBuildingsLoading(false);
      });
    return () => controller.abort();
  }, [location.hasValue, location.lat, location.lng]);
  const rememberMapState = () => {
    const snapshot = {
      lat: placeForm.lat,
      lng: placeForm.lng,
      footprintPoints: (placeForm.footprintPoints || []).map((point) => [...point]),
      footprintParts: (placeForm.footprintParts || []).map((part) =>
        part.map((point) => [...point]),
      ),
      frontagePoints: (placeForm.frontagePoints || []).map((point) => [...point]),
    };
    setUndoHistory((history) => [...history, snapshot].slice(-40));
  };
  const undoLastAction = () => {
    setUndoHistory((history) => {
      if (!history.length) return history;
      const snapshot = history[history.length - 1];
      setPlaceForm((current) => ({
        ...current,
        ...snapshot,
      }));
      setSnappedVertexIndex(null);
      setVertexDeleteMode(false);
      setPolygonDragMode(false);
      setRotationMode(false);
      setScaleMode(false);
      setShapeMode(null);
      return history.slice(0, -1);
    });
  };
  const removeVertex = (index, event) => {
    if (!vertexDeleteMode) return;
    if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
    rememberMapState();
    setPlaceForm((current) =>
      updateFootprintPart(current, activePartIndex, (points) =>
        points.filter((_, pointIndex) => pointIndex !== index),
      ),
    );
    setVertexDeleteMode(false);
    setSnappedVertexIndex(null);
  };
  const updateVertex = (index, latlng, applyMagnet = true) => {
    const draggedPoint = [latlng.lng, latlng.lat];
    const buildingSnap =
      applyMagnet && buildingMagnetEnabled
        ? findBuildingSnapPoint(draggedPoint, buildingLines, snapDistanceMeters)
        : null;
    const geometrySnap =
      !buildingSnap && geometryMagnetEnabled
        ? snapPointToPolygonDirections(activePoints, index, draggedPoint)
        : null;
    const nextPoint = buildingSnap?.point || geometrySnap?.point || draggedPoint;
    setPlaceForm((current) =>
      updateFootprintPart(current, activePartIndex, (points) =>
        points.map((point, pointIndex) =>
          pointIndex === index
            ? [Number(nextPoint[0].toFixed(7)), Number(nextPoint[1].toFixed(7))]
            : point,
        ),
      ),
    );
    setSnappedVertexIndex(buildingSnap || geometrySnap ? index : null);
  };
  const rotatePolygon = (event) => {
    const drag = rotationDragRef.current;
    if (!drag) return;
    const point = event.target.getLatLng();
    const nextAngle = Math.atan2(
      point.lat - drag.center.lat,
      (point.lng - drag.center.lng) * drag.lngScale,
    );
    const delta = nextAngle - drag.startAngle;
    const cos = Math.cos(delta);
    const sin = Math.sin(delta);
    const rotated = drag.points.map(([lng, lat]) => {
      const x = (lng - drag.center.lng) * drag.lngScale;
      const y = lat - drag.center.lat;
      return [
        Number((drag.center.lng + (x * cos - y * sin) / drag.lngScale).toFixed(7)),
        Number((drag.center.lat + x * sin + y * cos).toFixed(7)),
      ];
    });
    setPlaceForm((current) => updateFootprintPart(current, activePartIndex, () => rotated));
  };
  const scalePolygon = (event) => {
    const drag = scaleDragRef.current;
    if (!drag) return;
    const point = event.target.getLatLng();
    const distance = Math.hypot(
      (point.lng - drag.center.lng) * drag.lngScale,
      point.lat - drag.center.lat,
    );
    const factor = Math.min(10, Math.max(0.1, distance / drag.startDistance));
    const scaled = drag.points.map(([lng, lat]) => [
      Number((drag.center.lng + (lng - drag.center.lng) * factor).toFixed(7)),
      Number((drag.center.lat + (lat - drag.center.lat) * factor).toFixed(7)),
    ]);
    setPlaceForm((current) => updateFootprintPart(current, activePartIndex, () => scaled));
  };
  const updateLocationMarker = (event) => {
    const next = event.target.getLatLng();
    setPlaceForm((current) => ({
      ...current,
      lat: next.lat.toFixed(7),
      lng: next.lng.toFixed(7),
    }));
  };
  const clearFootprint = () => {
    rememberMapState();
    setPlaceForm((current) => ({
      ...current,
      footprintPoints: [],
      footprintParts: [],
    }));
  };
  const selectBuildingAsFootprint = (buildingLine) => {
    if (!buildingLine.isClosed) return;
    if (activePoints.length > 0 && !window.confirm('להחליף את החלק הנוכחי בגבולות המתחם שבחרת?')) {
      return;
    }
    rememberMapState();
    const points = buildingLine.points
      .slice(0, -1)
      .map(([lng, lat]) => [Number(lng.toFixed(7)), Number(lat.toFixed(7))]);
    setPlaceForm((current) => updateFootprintPart(current, activePartIndex, () => points));
    setSelectBuildingMode(false);
    setSnappedVertexIndex(null);
  };
  return (
    <Stack
      sx={{
        width: "100%",
        borderRadius: '14px',
        background: '#fff',
      }}
    >
      <Stack
        sx={{
          display: 'none',
          justifyContent: 'space-between',
          alignItems: 'end',
          gap: '12px',
          marginBottom: '14px',
        }}
      >
        <Stack>
          <Typography>מיקום וגבולות המקום</Typography>
        </Stack>
      </Stack>

      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(420px, 1fr) 260px',
          gap: '16px',
          height: '100%',
          gridTemplateRows: 'auto minmax(0, 1fr)',
        }}
      >
        <Stack
          role="toolbar"
          aria-label="כלי עריכת מפה"
          sx={(theme) => ({
            gridColumn: '1 / -1',
            position: 'relative',
            zIndex: '5',
            display: 'flex',
            justifyContent: 'flex-start',
            gap: '8px',
            minHeight: '52px',
            padding: '7px 10px',
            border: '1px solid var(--line)',
            borderRadius: '12px',
            background: '#fff',
            boxShadow: '0 5px 18px rgba(15, 23, 42, .08)',
            [theme.breakpoints.down(681)]: {
              overflowX: 'auto',
              justifyContent: 'flex-start',
            },
          })}
        >
          {!polygonOnly && (
            <Stack
              direction="row"
              sx={{
                gap: '5px',
                alignItems: 'center',
              }}
            >
              <MapToolButton
                label="עריכת נקודת מיקום"
                icon="location"
                active={editMode === 'location'}
                onClick={() => {
                  setEditMode('location');
                  setSelectBuildingMode(false);
                  setSnappedVertexIndex(null);
                }}
              />
              <MapToolButton
                label="עריכת פוליגון"
                icon="polygon"
                active={editMode === 'polygon'}
                onClick={() => setEditMode('polygon')}
              />
              <MapToolButton
                label="חזית"
                icon="frontage"
                active={editMode === 'frontage'}
                onClick={() => {
                  setEditMode('frontage');
                  setSelectBuildingMode(false);
                  setVertexDeleteMode(false);
                  setPolygonDragMode(false);
                  setShapeMode(null);
                }}
              />
            </Stack>
          )}
          {editMode === 'frontage' && (
            <Stack
              direction="row"
              aria-label="עריכת חזית"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <MapToolButton
                label="ביטול הפעולה האחרונה"
                icon="undo"
                disabled={!undoHistory.length}
                onClick={undoLastAction}
              />
              <MapToolButton
                label="מחיקת החזית"
                icon="trash"
                danger
                disabled={!placeForm.frontagePoints?.length}
                onClick={() => {
                  rememberMapState();
                  setPlaceForm((current) => ({
                    ...current,
                    frontagePoints: [],
                  }));
                }}
              />
            </Stack>
          )}
          {editMode === 'polygon' && (
            <Stack
              direction="row"
              sx={{
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Stack
                direction="row"
                aria-label="יצירת צורה"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  position: 'relative',
                }}
              >
                <MapToolButton
                  label={
                    shapeMode
                      ? `מצב יצירת ${shapeMode === 'square' ? 'ריבוע' : shapeMode === 'rectangle' ? 'מלבן' : 'עיגול'}`
                      : 'יצירת צורה'
                  }
                  icon={shapeMode || 'shapes'}
                  active={Boolean(shapeMode)}
                  onClick={() => setShapeMenuOpen((open) => !open)}
                  aria-expanded={shapeMenuOpen}
                />
                {shapeMenuOpen && (
                  <Stack
                    role="menu"
                    aria-label="בחירת צורה"
                    sx={{
                      position: 'absolute',
                      top: 'calc(100% + 10px)',
                      right: '0',
                      zIndex: '1100',
                      display: 'grid',
                      minWidth: '150px',
                      padding: '6px',
                      border: '1px solid #d0d5dd',
                      borderRadius: '10px',
                      background: '#fff',
                      boxShadow: '0 12px 28px rgba(15, 23, 42, .18)',
                      '& button': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: '9px',
                        width: '100%',
                        minHeight: '38px',
                        padding: '7px 9px',
                        border: '0',
                        borderRadius: '7px',
                        color: '#344054',
                        background: 'transparent',
                        boxShadow: 'none',
                        fontSize: '13px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap',
                      },
                      '& button:hover': {
                        color: '#175cd3',
                        background: '#eff8ff',
                        transform: 'none',
                      },
                      '& svg': {
                        width: '19px',
                        height: '19px',
                        fill: 'none',
                        stroke: 'currentColor',
                        strokeWidth: '1.8',
                      },
                    }}
                  >
                    {[
                      ['square', 'ריבוע'],
                      ['rectangle', 'מלבן'],
                      ['circle', 'עיגול'],
                    ].map(([shape, label]) => (
                      <Button
                        type="button"
                        role="menuitem"
                        key={shape}
                        onClick={() => {
                          setShapeMode(shape);
                          setShapeMenuOpen(false);
                          setVertexDeleteMode(false);
                          setPolygonDragMode(false);
                          setSelectBuildingMode(false);
                        }}
                        sx={String(shapeMode === shape ? 'active' : '')
                          .split(/\s+/)
                          .map(
                            (name) =>
                              ({
                                active: {
                                  background: '#1570ef',
                                  borderColor: '#1570ef',
                                  '& small': {
                                    color: '#ddebff',
                                  },
                                  color: '#fff',
                                  boxShadow: '0 4px 10px rgba(21, 112, 239, .25)',
                                  borderBottomColor: 'var(--primary)',
                                  transform: 'none',
                                },
                              })[name],
                          )
                          .filter(Boolean)}
                      >
                        <MapToolIcon name={shape} />
                        <Typography>{label}</Typography>
                      </Button>
                    ))}
                    {shapeMode && (
                      <Button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setShapeMode(null);
                          setShapeMenuOpen(false);
                        }}
                      >
                        <Typography>ביטול כלי הצורה</Typography>
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>

              <Stack
                direction="row"
                aria-label="מגנטים ויישור"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <MapToolButton
                  label="מגנט זעיר לקווים מקבילים וזוויות ישרות"
                  icon="align"
                  active={geometryMagnetEnabled}
                  onClick={() => {
                    setGeometryMagnetEnabled((enabled) => !enabled);
                    setSnappedVertexIndex(null);
                  }}
                />
                <MapToolButton
                  label="מגנט עדין לקווי מבנה"
                  icon="building"
                  active={buildingMagnetEnabled}
                  onClick={() => {
                    setBuildingMagnetEnabled((enabled) => !enabled);
                    setSnappedVertexIndex(null);
                  }}
                />
                <MapToolButton
                  label={selectBuildingMode ? 'ביטול בחירת מבנה מהמפה' : 'בחירת מבנה קיים מהמפה'}
                  icon="select"
                  active={selectBuildingMode}
                  disabled={buildingsLoading || !buildingLines.some((line) => line.isClosed)}
                  onClick={() => {
                    setSelectBuildingMode((enabled) => !enabled);
                    setVertexDeleteMode(false);
                    setPolygonDragMode(false);
                    setShapeMode(null);
                  }}
                />
              </Stack>

              <Stack
                direction="row"
                aria-label="עריכה והזזה"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <MapToolButton
                  label="ביטול הפעולה האחרונה"
                  icon="undo"
                  disabled={!undoHistory.length}
                  onClick={undoLastAction}
                />
                <MapToolButton
                  label="מחיקת נקודה — לחצו ואז בחרו נקודה"
                  icon="removeVertex"
                  active={vertexDeleteMode}
                  disabled={!activePoints.length}
                  onClick={() => {
                    setVertexDeleteMode((enabled) => !enabled);
                    setPolygonDragMode(false);
                    setRotationMode(false);
                    setScaleMode(false);
                    setSelectBuildingMode(false);
                    setShapeMode(null);
                  }}
                />
                <MapToolButton
                  label="הזזת כל הפוליגון"
                  icon="hand"
                  active={polygonDragMode}
                  disabled={!footprintParts.some((part) => part.length)}
                  onClick={() => {
                    setPolygonDragMode((enabled) => !enabled);
                    setRotationMode(false);
                    setScaleMode(false);
                    setVertexDeleteMode(false);
                    setSelectBuildingMode(false);
                    setShapeMode(null);
                  }}
                />
                <MapToolButton
                  label="סיבוב הפוליגון — הפעילו וגררו את הידית לידו"
                  icon="rotate"
                  active={rotationMode}
                  disabled={activePoints.length < 3}
                  onClick={() => {
                    setRotationMode((enabled) => !enabled);
                    setScaleMode(false);
                    setPolygonDragMode(false);
                    setVertexDeleteMode(false);
                    setSelectBuildingMode(false);
                    setShapeMode(null);
                  }}
                />
                <MapToolButton
                  label="הגדלה והקטנה בלי לשנות צורה — הפעילו וגררו את הידית"
                  icon="scale"
                  active={scaleMode}
                  disabled={activePoints.length < 3}
                  onClick={() => {
                    setRotationMode(false);
                    setPolygonDragMode(false);
                    setVertexDeleteMode(false);
                    setSelectBuildingMode(false);
                    setShapeMode(null);
                    setScaleMode((enabled) => !enabled);
                  }}
                />
                <MapToolButton
                  label="מחיקת כל הפוליגון"
                  icon="trash"
                  danger
                  disabled={!footprintParts.some((part) => part.length)}
                  onClick={clearFootprint}
                />
              </Stack>
            </Stack>
          )}
        </Stack>

        <MapContainer
          className={`map-box leaflet-map-box${polygonDragMode ? ' polygon-drag-mode' : ''}${shapeMode ? ' shape-draw-mode' : ''}`}
          center={[location.lat, location.lng]}
          zoom={defaultMapZoom}
          minZoom={minMapZoom}
          maxZoom={maxMapZoom}
          zoomControl={false}
          attributionControl={false}
          scrollWheelZoom={false}
          rotate
          touchRotate
          shiftKeyRotate
          rotateControl={false}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap contributors &copy; CARTO"
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}.png"
            subdomains="abc"
            maxNativeZoom={maxTileZoom}
            maxZoom={maxMapZoom}
          />

          <MapBearingGestures />
          <MapEditorEvents
            editMode={editMode}
            setPlaceForm={setPlaceForm}
            selectBuildingMode={selectBuildingMode}
            buildingLines={buildingLines}
            onSelectBuilding={selectBuildingAsFootprint}
            activePartIndex={activePartIndex}
            geometryMagnetEnabled={geometryMagnetEnabled}
            polygonDragMode={polygonDragMode}
            interactionLocked={rotationMode || scaleMode}
            shapeMode={shapeMode}
            onShapeComplete={() => setShapeMode(null)}
            onBeforeChange={rememberMapState}
          />
          <MapFootprintFocus request={focusRequest} />
          <ZoomControl position="bottomright" />
          <AttributionControl position="bottomleft" prefix={false} />

          {editMode === 'polygon' &&
            (buildingMagnetEnabled || selectBuildingMode) &&
            buildingLines.map((buildingLine) => {
              const positions = buildingLine.points.map(([lng, lat]) => [lat, lng]);
              const pathOptions = {
                color: selectBuildingMode && buildingLine.isClosed ? '#087443' : '#1570ef',
                weight: selectBuildingMode && buildingLine.isClosed ? 3 : 1.5,
                opacity: selectBuildingMode && !buildingLine.isClosed ? 0.25 : 0.65,
                dashArray: selectBuildingMode && buildingLine.isClosed ? undefined : '3 4',
                fillColor: '#12b76a',
                fillOpacity: selectBuildingMode && buildingLine.isClosed ? 0.12 : 0,
                bubblingMouseEvents: selectBuildingMode,
              };
              return buildingLine.isClosed ? (
                <Polygon
                  key={buildingLine.id}
                  positions={positions}
                  interactive={selectBuildingMode}
                  pathOptions={pathOptions}
                />
              ) : (
                <Polyline
                  key={buildingLine.id}
                  positions={positions}
                  interactive={false}
                  pathOptions={pathOptions}
                />
              );
            })}

          {nearbyFootprints.flatMap((nearbyPlace) =>
            footprintRings(nearbyPlace.footprint).map((ring, ringIndex) => (
              <Polygon
                key={`${nearbyPlace.placeId}-${ringIndex}`}
                positions={ring.map(([lng, lat]) => [lat, lng])}
                pathOptions={{
                  color: '#667085',
                  weight: 2,
                  dashArray: '6 5',
                  fillColor: '#98a2b3',
                  fillOpacity: 0.24,
                  bubblingMouseEvents: false,
                }}
              >
                <Tooltip permanent direction="center" className="nearby-footprint-label">
                  {placeLabel(nearbyPlace)}
                </Tooltip>
              </Polygon>
            )),
          )}

          {footprintParts.map(
            (part, partIndex) =>
              part.length >= 3 && (
                <Polygon
                  key={`footprint-part-${partIndex}`}
                  positions={part.map(([lng, lat]) => [lat, lng])}
                  pathOptions={{
                    color: partIndex === activePartIndex ? '#d93622' : '#8f2417',
                    weight: partIndex === activePartIndex ? 3 : 2,
                    fillColor: '#ff4b35',
                    fillOpacity: partIndex === activePartIndex ? 0.42 : 0.25,
                  }}
                />
              ),
          )}

          {placeForm.frontagePoints?.length >= 2 && (
            <Polyline
              positions={placeForm.frontagePoints.map(([lng, lat]) => [lat, lng])}
              pathOptions={{
                color: '#1570ef',
                weight: 6,
                opacity: 0.9,
              }}
            >
              <Tooltip permanent direction="center">
                חזית
              </Tooltip>
            </Polyline>
          )}

          {editMode === 'frontage' &&
            (placeForm.frontagePoints || []).map(([lng, lat], index) => (
              <Marker
                key={`frontage-${index}`}
                position={[lat, lng]}
                icon={vertexIcon}
                draggable
                autoPan={false}
                autoPanOnFocus={false}
                keyboard={false}
                eventHandlers={{
                  dragstart() {
                    rememberMapState();
                  },
                  drag(event) {
                    const point = event.target.getLatLng();
                    setPlaceForm((current) => ({
                      ...current,
                      frontagePoints: current.frontagePoints.map((item, itemIndex) =>
                        itemIndex === index
                          ? [Number(point.lng.toFixed(7)), Number(point.lat.toFixed(7))]
                          : item,
                      ),
                    }));
                  },
                }}
              />
            ))}

          {editMode === 'polygon' &&
            activePoints.map(([lng, lat], index) => (
              <Marker
                key={index}
                position={[lat, lng]}
                icon={vertexIcon}
                autoPan={false}
                autoPanOnFocus={false}
                keyboard={false}
                draggable={
                  !vertexDeleteMode && !polygonDragMode && !rotationMode && !scaleMode && !shapeMode
                }
                bubblingMouseEvents={false}
                opacity={snappedVertexIndex === index ? 0.72 : 1}
                eventHandlers={{
                  mousedown(event) {
                    if (event.originalEvent) L.DomEvent.preventDefault(event.originalEvent);
                  },
                  click(event) {
                    removeVertex(index, event);
                  },
                  dragstart() {
                    dragHistoryCapturedRef.current = false;
                  },
                  drag(event) {
                    if (!dragHistoryCapturedRef.current) {
                      rememberMapState();
                      dragHistoryCapturedRef.current = true;
                    }
                    updateVertex(index, event.target.getLatLng(), false);
                  },
                  dragend(event) {
                    updateVertex(index, event.target.getLatLng(), true);
                    dragHistoryCapturedRef.current = false;
                  },
                }}
              />
            ))}

          {editMode === 'polygon' && rotationMode && rotationGeometry && (
            <Marker
              position={rotationGeometry.handle}
              icon={rotationHandleIcon}
              autoPan={false}
              autoPanOnFocus={false}
              keyboard={false}
              draggable
              bubblingMouseEvents={false}
              zIndexOffset={1200}
              eventHandlers={{
                dragstart(event) {
                  rememberMapState();
                  const point = event.target.getLatLng();
                  rotationDragRef.current = {
                    center: rotationGeometry.center,
                    lngScale: rotationGeometry.lngScale,
                    points: activePoints.map((item) => [...item]),
                    startAngle: Math.atan2(
                      point.lat - rotationGeometry.center.lat,
                      (point.lng - rotationGeometry.center.lng) * rotationGeometry.lngScale,
                    ),
                  };
                },
                drag: rotatePolygon,
                dragend() {
                  rotationDragRef.current = null;
                },
              }}
            >
              <Tooltip direction="top" permanent>
                גררו לסיבוב
              </Tooltip>
            </Marker>
          )}

          {editMode === 'polygon' && scaleMode && rotationGeometry && (
            <Marker
              position={rotationGeometry.scaleHandle}
              icon={scaleHandleIcon}
              autoPan={false}
              autoPanOnFocus={false}
              keyboard={false}
              draggable
              bubblingMouseEvents={false}
              zIndexOffset={1200}
              eventHandlers={{
                dragstart() {
                  rememberMapState();
                  scaleDragRef.current = {
                    center: rotationGeometry.center,
                    lngScale: rotationGeometry.lngScale,
                    points: activePoints.map((item) => [...item]),
                    startDistance: Math.hypot(
                      (rotationGeometry.scaleHandle[1] - rotationGeometry.center.lng) *
                      rotationGeometry.lngScale,
                      rotationGeometry.scaleHandle[0] - rotationGeometry.center.lat,
                    ),
                  };
                },
                drag: scalePolygon,
                dragend() {
                  scaleDragRef.current = null;
                },
              }}
            >
              <Tooltip direction="top" permanent>
                גררו לשינוי גודל
              </Tooltip>
            </Marker>
          )}

          <Marker
            position={[location.lat, location.lng]}
            icon={locationIcon}
            autoPan={false}
            autoPanOnFocus={false}
            keyboard={false}
            draggable={
              (editMode === 'location' || allowMarkerMoveInPolygonMode) &&
              !vertexDeleteMode &&
              !polygonDragMode &&
              !rotationMode &&
              !scaleMode &&
              !shapeMode
            }
            eventHandlers={{
              dragstart() {
                dragHistoryCapturedRef.current = false;
              },
              drag(event) {
                if (!dragHistoryCapturedRef.current) {
                  rememberMapState();
                  dragHistoryCapturedRef.current = true;
                }
                updateLocationMarker(event);
              },
              dragend(event) {
                updateLocationMarker(event);
                dragHistoryCapturedRef.current = false;
              },
            }}
          />
        </MapContainer>

        <Stack
          sx={{
            display: 'grid',
            alignContent: 'start',
            gap: '12px',
          }}
        >
          {editMode === 'location' && (
            <>
              <Stack>
                Latitude
                <TextField
                  dir="ltr"
                  type="number"
                  step="any"
                  value={placeForm.lat}
                  onChange={(e) =>
                    setPlaceForm({
                      ...placeForm,
                      lat: e.target.value,
                    })
                  }
                  fullWidth
                />
              </Stack>
              <Stack>
                Longitude
                <TextField
                  dir="ltr"
                  type="number"
                  step="any"
                  value={placeForm.lng}
                  onChange={(e) =>
                    setPlaceForm({
                      ...placeForm,
                      lng: e.target.value,
                    })
                  }
                  fullWidth
                />
              </Stack>
              {googleMapsUrl && (
                <Link
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    minHeight: '40px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    padding: '0 16px',
                    background: 'var(--primary)',
                    color: 'white',
                    fontWeight: '700',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background: 'var(--primary-dark)',
                    },
                  }}
                >
                  פתיחה ב-Google Maps
                </Link>
              )}
            </>
          )}
          {editMode === 'polygon' && (
            <Stack
              sx={{
                display: 'grid',
                gap: '8px',
                paddingTop: '4px',
                '& strong': {
                  fontSize: '13px',
                },
                '& button': {
                  width: '100%',
                },
              }}
            >
              {polygonPanelContent}
              {!singlePart && (
                <Stack
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    alignItems: 'end',
                    gap: '8px',
                    width: '100%',
                    minWidth: '0',
                    '& label': {
                      display: 'grid',
                      minWidth: '0',
                      gap: '4px',
                      fontSize: '12px',
                      fontWeight: '700',
                    },
                    '& select': {
                      width: '100%',
                      minWidth: '0',
                    },
                  }}
                >
                  <Stack>
                    <Typography>חלק פעיל</Typography>
                    <TextField
                      value={activePartIndex}
                      onChange={(event) => setActivePartIndex(Number(event.target.value))}
                      select
                      fullWidth
                    >
                      {footprintParts.map((part, index) => (
                        <MenuItem key={index} value={index}>
                          חלק {index + 1} - {part.length} נקודות
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <Button
                    type="button"
                    onClick={() => {
                      rememberMapState();
                      setPlaceForm((current) => {
                        const parts = current.footprintParts?.length
                          ? [...current.footprintParts, []]
                          : [current.footprintPoints || [], []];
                        return {
                          ...current,
                          footprintParts: parts,
                          footprintPoints: [],
                        };
                      });
                      setActivePartIndex(footprintParts.length);
                    }}
                    sx={{
                      background: '#edf2f7',
                      color: 'var(--text)',
                      '&:hover': {
                        background: '#ddebff',
                        color: '#1269e8',
                      },
                      width: 'auto',
                      minWidth: 'max-content',
                      paddingInline: '12px',
                    }}
                  >
                    + הוספת חלק
                  </Button>
                </Stack>
              )}
              <Typography>
                {activePoints.length} נקודות{singlePart ? '' : ` בחלק ${activePartIndex + 1}`}
              </Typography>{' '}
              {nearbyFootprintsLoading && <Typography>טוען פוליגונים סמוכים…</Typography>}
              {showHeightControl && (
                <Stack
                  sx={{
                    display: 'grid',
                    gap: '6px',
                    padding: '10px',
                    border: '1px solid #d8e0e8',
                    borderRadius: '10px',
                    background: '#f8fafc',
                    fontWeight: '600',
                    '& select': {
                      width: '100%',
                    },
                    '& small': {
                      color: '#667085',
                      fontWeight: '400',
                    },
                  }}
                >
                  <Typography>גובה הפוליגון במפה</Typography>
                  <TextField
                    value={placeForm.footprintHeightLevel}
                    onChange={(event) => {
                      rememberMapState();
                      setPlaceForm((current) => ({
                        ...current,
                        footprintHeightLevel: Number(event.target.value),
                      }));
                    }}
                    select
                    fullWidth
                  >
                    <MenuItem value={0}>0 - שטוח</MenuItem>
                    <MenuItem value={1}>1 - נמוך</MenuItem>
                    <MenuItem value={2}>2 - בינוני</MenuItem>
                    <MenuItem value={3}>3 - גבוה</MenuItem>
                  </TextField>
                </Stack>
              )}
              {selectBuildingMode && (
                <Typography
                  sx={{
                    color: '#087443',
                    fontWeight: '700',
                  }}
                >
                  לחץ על המבנה הירוק שברצונך להפוך לפוליגון.
                </Typography>
              )}
              {activePoints.length > 0 && footprintParts.length > 1 && (
                <Button
                  type="button"
                  onClick={() => {
                    rememberMapState();
                    setPlaceForm((current) => {
                      const parts = (current.footprintParts || []).filter(
                        (_, index) => index !== activePartIndex,
                      );
                      return {
                        ...current,
                        footprintParts: parts,
                        footprintPoints: parts[Math.max(0, activePartIndex - 1)] || [],
                      };
                    });
                    setActivePartIndex((index) => Math.max(0, index - 1));
                  }}
                  sx={{
                    color: '#b42318',
                    borderColor: '#f1b9b4',
                    background: '#fff5f4',
                  }}
                >
                  מחיקת החלק הנוכחי
                </Button>
              )}
              {originalPoints.length >= 3 && (
                <Button
                  type="button"
                  onClick={() => {
                    rememberMapState();
                    setPlaceForm((current) => ({
                      ...current,
                      footprintPoints: originalPoints.map((point) => [...point]),
                      footprintParts: [originalPoints.map((point) => [...point])],
                    }));
                  }}
                  sx={{
                    background: '#edf2f7',
                    color: 'var(--text)',
                    '&:hover': {
                      background: '#ddebff',
                      color: '#1269e8',
                    },
                  }}
                >
                  חזרה לפוליגון המקורי
                </Button>
              )}
              {activePoints.length > 0 && activePoints.length < 3 && (
                <Typography
                  sx={{
                    color: '#b54708',
                  }}
                >
                  נדרשות לפחות 3 נקודות כדי לשמור פוליגון.
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
export default PolygonEditor;
